import { sameAddress } from '@/client/confs/app';
import { AuthedButton } from '@/client/components/authed-button';
import { CurrencyView, CurrencyViewProps } from '@/client/components/currency-view';
import { UserAvatar } from '@/client/components/user-avatar';
import { useAuth } from '@/client/hooks/use-auth';
import { useSignedUserAssets } from '@/client/hooks/use-signed-user-assets';
import { useSignedUserInfo } from '@/client/hooks/use-signed-user-info';
import { useSingerTokenBalance } from '@/client/hooks/use-signed-token-balance';
import { useTheme } from '@/client/hooks/use-theme';
import { useAppSettingStore } from '@/client/store/app-store';
import { useModelStore } from '@/client/store/model-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useTranslation } from 'react-i18next';
import useMedia from 'react-use/lib/useMedia';

export const AssetsCurrencyView = ({ title, pnl, className, ...props }: { title: string; pnl?: string; className?: string } & CurrencyViewProps) => {
  return (
    <dl className={cn('flex flex-col text-left', className)}>
      <dt className="text-foreground/70 text-xs leading-4 font-medium whitespace-nowrap">{title}</dt>
      <dd className="text-sm leading-5 md:text-base md:leading-5 font-semibold flex items-center gap-1 whitespace-nowrap tabular-nums">
        <CurrencyView showIcon={true} animated={true} {...props} />
        {pnl && Math.abs(Number(pnl)) > 0 && (
          <CurrencyView
            className="text-xs leading-4"
            animated={true}
            {...props}
            isChange={true}
            value={pnl}
            decimal={Number(props.decimal ?? 18) + 2}
          />
        )}
      </dd>
    </dl>
  );
};

export const UserInfo = () => {
  const { t } = useTranslation();
  const setWalletOpen = useModelStore(state => state.setWalletOpen);
  const setDepositOpen = useModelStore(state => state.setDepositOpen);
  const { isAuthed, login } = useAuth();
  const { data: assets } = useSignedUserAssets(api);
  const { data: singerInfo } = useSignedUserInfo(api);
  const baseToken = useAppSettingStore(state => state.baseToken);
  const tokenBalances = useSingerTokenBalance(api);
  const onChainBalance = tokenBalances.data?.find(item => sameAddress(item.baseTokenAddress, baseToken.address));
  const isDesktop = useMedia('(min-width: 768px)');
  const { theme } = useTheme();

  // Not authenticated: show Sign In button only
  if (!isAuthed) {
    return (
      <AuthedButton
        variant="default"
        onClick={login}
        size={isDesktop ? 'default' : 'sm'}
        className="bg-primary hover:bg-primary/90"
      >
        {t('sign-in')}
      </AuthedButton>
    );
  }

  // Authenticated: show Deposit, Portfolio, Balance, and Avatar
  return (
    <>
      {isDesktop && singerInfo && (
        <AuthedButton
          variant={theme === 'dark' ? 'secondary' : 'default'}
          showAuth={false}
          showLoading={false}
          onClick={e => {
            e.stopPropagation();
            setDepositOpen(true);
          }}
        >
          {t('deposit')}
        </AuthedButton>
      )}
      <AuthedButton
        showAuth={false}
        showLoading={false}
        variant="ghost"
        onClick={() => setWalletOpen(true)}
        className="px-0 h-full flex items-center justify-center hover:bg-transparent gap-4 md:gap-6"
      >
        <AssetsCurrencyView title={t('portfolio')} value={assets?.portfolio ?? 0} decimal={assets?.decimal} className="min-w-[80px]" />
        <AssetsCurrencyView title={t('balance')} value={onChainBalance?.balance} decimal={onChainBalance?.baseTokenDecimals ?? assets?.decimal} isLoading={tokenBalances.isPending || !onChainBalance} className="min-w-[64px]" />
      </AuthedButton>
      <AuthedButton
        showLoading={!singerInfo}
        variant="ghost"
        onClick={() => setWalletOpen(true)}
        size={isDesktop ? undefined : 'sm'}
        className={cn(singerInfo != undefined && 'px-0 bg-transparent hover:bg-transparent')}
      >
        <UserAvatar
          avatar={singerInfo?.avatar}
          address={singerInfo?.uid}
          fallback={singerInfo?.name}
          className="size-9 cursor-pointer duration-200 hover:ring-2 hover:ring-offset-2 ring-offset-background ring-blue-[#C0D4FF]"
        />
      </AuthedButton>
    </>
  );
};
